/**
 * Capture sessions for a project: list them, and create one.
 *
 * The purpose field matters more than it looks. A `correction` session is how
 * the backend knows this capture was an attempt to fix a gap, and the project
 * merge reports whether it actually improved anything. Choosing it here is
 * therefore a real decision, not a label - which is why the form explains it
 * rather than defaulting silently.
 */

import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  createCaptureSession,
  getPairingAvailability,
  getProjectCoverage,
  listCaptureSessions,
} from "@/api/client";
import { ApiError } from "@/api/errors";
import { CAPTURE_PURPOSES, CAPTURE_TYPES, type CapturePurpose, type CaptureType } from "@/api/types";
import { Field } from "@/components/Field";
import { Button, Callout, EmptyState, Loading } from "@/components/ui";
import { ErrorState } from "@/components/ErrorState";
import {
  CAPTURE_PURPOSE_LABELS,
  CAPTURE_TYPE_LABELS,
  NOT_YET,
} from "@/i18n/terms";
import { useAction, useAsync } from "@/lib/useAsync";
import { PhonePairingDialog } from "@/features/pairing/PhonePairingDialog";
import { CaptureSummarySection } from "./CaptureSummarySection";
import { NewCaptureMenu } from "./NewCaptureMenu";
import { SessionRow } from "./SessionRow";
import { useCaptureSummary } from "./useCaptureSummary";
import { PageHeader } from "@/components/product";
import { useProjectContext } from "@/features/projects/ProjectScreen";
import "./sessions.css";

export function ProjectSessionsTab() {
  const { project } = useProjectContext();
  const [params] = useSearchParams();
  // The coverage screen links here with ?amac=correction when a region needs
  // more capture, so the form opens already set up for that.
  const suggestedPurpose: CapturePurpose =
    params.get("amac") === "correction" ? "correction" : "initial";
  const [showForm, setShowForm] = useState(suggestedPurpose === "correction");
  const [menuOpen, setMenuOpen] = useState(false);
  // Which menu item opened the form. "photo" when the form was opened any
  // other way - a correction shoot that nobody chose a type for is a photo
  // session until told otherwise, which is what it was before this existed.
  const [formCaptureType, setFormCaptureType] = useState<CaptureType>("photo");
  const [pairingOpen, setPairingOpen] = useState(false);
  const coverageSupported =
    project.capabilities?.semantic_coverage ?? project.subject_type === "human_head";

  // The capture menu offers phone capture only when the server says pairing
  // can actually work here; otherwise the item would open a dialog that
  // immediately fails.
  const availability = useCallback(
    (signal: AbortSignal) => getPairingAvailability({ signal }),
    [],
  );
  const { state: pairing } = useAsync(availability);
  const pairingUsable =
    pairing.status === "success" && pairing.data.enabled && pairing.data.configured;

  const fetcher = useCallback(
    (signal: AbortSignal) => listCaptureSessions(project.id, { limit: 100 }, { signal }),
    [project.id],
  );
  const { state, reload } = useAsync(fetcher);
  const sessions = state.status === "success" ? state.data : null;

  // One media request per session, hoisted out of the rows so the aggregate at
  // the bottom and the counts on the rows are the same numbers.
  const summary = useCaptureSummary(sessions);

  // Coverage carries both the coverage figures *and* the backend's own count
  // of analysed sessions, so the summary needs one request rather than two.
  const coverageFetcher = useCallback(
    (signal: AbortSignal) => coverageSupported
      ? getProjectCoverage(project.id, {}, { signal })
      : Promise.reject(new Error("unsupported")),
    [project.id, coverageSupported],
  );
  const { state: coverageState } = useAsync(coverageFetcher);
  // A 404 means never computed - an empty state, not a failure to apologise
  // for. Any other error leaves both flags false and the tile stays quiet.
  const coverageMissing =
    coverageState.status === "error" &&
    coverageState.error instanceof ApiError &&
    coverageState.error.kind === "not_found";

  return (
    <div className="stack">
      <PageHeader
        title="Çekimler"
        subtitle={project.name}
        actions={
          <NewCaptureMenu
            onUpload={(captureType) => {
              setFormCaptureType(captureType);
              setShowForm(true);
              setMenuOpen(false);
            }}
            onPhone={() => {
              setPairingOpen(true);
              setMenuOpen(false);
            }}
            open={menuOpen}
            setOpen={setMenuOpen}
            pairingUsable={pairingUsable}
          />
        }
      />

      {/*
        The pairing dialog is the same component the phone-pairing phase
        shipped and real-device validated. It is opened from the capture menu
        rather than reimplemented, so the protocol, its single-use secret and
        its revocation semantics are untouched.
      */}
      {pairingOpen && (
        <PhonePairingDialog
          projectId={project.id}
          onClose={() => setPairingOpen(false)}
        />
      )}

      <Callout>{NOT_YET.liveCamera}</Callout>

      {showForm && (
        <NewSessionForm
          projectId={project.id}
          defaultCaptureType={formCaptureType}
          defaultPurpose={suggestedPurpose}
          onCreated={() => {
            setShowForm(false);
            reload();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {state.status === "loading" && state.data === null && <Loading />}
      {state.status === "error" && <ErrorState error={state.error} onRetry={reload} />}

      {state.data !== null &&
        (state.data.length === 0 ? (
          /*
            A brand-new project lands here, and this is the first screen it
            gets. "Bir oturum oluşturup..." described a mechanism and offered
            nothing to press, which left the two real routes - upload what you
            already have, or shoot new frames with a phone - buried in a split
            menu in the header. Both are here now, named for what they do, with
            the one sentence that separates them.

            The phone route is absent rather than disabled when pairing is not
            configured: the same rule the capture menu follows, because a dead
            button explaining that a feature is not installed is furniture.
          */
          <EmptyState
            title="Sıradaki adım: fotoğrafları ekleyin"
            description={
              pairingUsable
                ? "Bilgisayarınızda hazır fotoğraflar varsa yükleyin. Yeni fotoğraflar çekecekseniz telefonunuzu bu projeye bağlayın."
                : "Bilgisayarınızdaki fotoğrafları yükleyerek başlayın."
            }
            action={
              <>
                <Button
                  variant="primary"
                  onClick={() => {
                    setFormCaptureType("photo");
                    setShowForm(true);
                  }}
                >
                  Fotoğraf Yükle
                </Button>
                {pairingUsable && (
                  <Button onClick={() => setPairingOpen(true)}>
                    Telefonla Çekime Başla
                  </Button>
                )}
              </>
            }
          />
        ) : (
          <ul className="sessionlist">
            {state.data.map((session) => (
              <li key={session.id}>
                <SessionRow
                  session={session}
                  assets={summary.bySession[session.id] ?? null}
                  counts={summary.countsBySession[session.id] ?? null}
                  onDeleted={reload}
                />
              </li>
            ))}
          </ul>
        ))}

      {/* Shown once there is something to summarise. An empty project already
          has an empty state above and does not need a panel of zeroes. */}
      {sessions !== null && sessions.length > 0 && (
        <CaptureSummarySection
          projectId={project.id}
          sessionCount={sessions.length}
          summary={summary}
          coverage={coverageState.status === "success" ? coverageState.data : null}
          coverageMissing={coverageMissing}
          coverageSupported={coverageSupported}
          coverageKind={project.perception_profile?.coverage_kind ?? "spatial_viewpoint_v1"}
        />
      )}
    </div>
  );
}

function NewSessionForm({
  projectId,
  defaultCaptureType,
  defaultPurpose,
  onCreated,
  onCancel,
}: {
  projectId: string;
  /** Whatever the menu item said. Still editable - it is a starting point,
   *  not a lock, and a shoot that turns out mixed can be changed here. */
  defaultCaptureType: CaptureType;
  defaultPurpose: CapturePurpose;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [captureType, setCaptureType] = useState<CaptureType>(defaultCaptureType);
  const [purpose, setPurpose] = useState<CapturePurpose>(defaultPurpose);
  const [notes, setNotes] = useState("");

  const { run, busy, error } = useAction(
    useCallback(
      (payload: Parameters<typeof createCaptureSession>[1]) =>
        createCaptureSession(projectId, payload),
      [projectId],
    ),
  );

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const created = await run({
      capture_type: captureType,
      purpose,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
    if (created) onCreated();
  };

  return (
    <form className="card form" onSubmit={submit}>
      <h3>Yeni çekim oturumu</h3>

      <Field label="Çekim türü" required hint="Bu oturuma ne yükleyeceksiniz?">
        {(props) => (
          <select
            {...props}
            value={captureType}
            onChange={(event) => setCaptureType(event.target.value as CaptureType)}
          >
            {CAPTURE_TYPES.map((value) => (
              <option key={value} value={value}>
                {CAPTURE_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field
        label="Amaç"
        required
        hint="Düzeltme çekimi, eksik kalan bir bölgeyi tamamlamak için yapılan ek çekimdir."
      >
        {(props) => (
          <select
            {...props}
            value={purpose}
            onChange={(event) => setPurpose(event.target.value as CapturePurpose)}
          >
            {CAPTURE_PURPOSES.map((value) => (
              <option key={value} value={value}>
                {CAPTURE_PURPOSE_LABELS[value]}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field label="Not" hint="İsteğe bağlı.">
        {(props) => (
          <textarea {...props} value={notes} onChange={(event) => setNotes(event.target.value)} />
        )}
      </Field>

      {error && <ErrorState error={error} compact />}

      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          Oluştur
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Vazgeç
        </Button>
      </div>
    </form>
  );
}
